// src/components/layout/MobileMenu.tsx
// ============================================================
// SALT — Mobile Navigation Menu
// Portaled slide-out drawer. Includes game selector tiles,
// nav links, social icons, notification link, and auth/account
// actions.
// ============================================================

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import NavLink from './NavLink';
import { AVAILABLE_GAMES, type GameEntry } from './GameSelector';
import { SOCIAL_LINKS, XIcon, DiscordIcon } from './SocialIcons';
import { useAuthContext } from '../../context/AuthContext';
import { useOverlayFocus } from '../../hooks/useOverlayFocus';

// ── Types ──────────────────────────────────────────────────

interface NavLinkDef {
  label: string;
  to: string;
  exact?: boolean;
}

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  primaryLinks: NavLinkDef[];
  currentGameCode: string | null;
  isLoggedIn: boolean;
  onTeamClick: () => void;
  onLogoutClick: () => void;
  /** Unread notification count from Navbar's useNotifications hook */
  unreadNotificationCount?: number;
}

// ── Subdomain URL builder (mirrors GameSelector) ───────────

const SUBDOMAIN_BASE =
  process.env.REACT_APP_SUBDOMAIN_BASE || '.playsalt.gg';

const VALID_GAME_CODES = ['hi', 'h3'];

function buildGameUrl(gameCode: string): string {
  // Validate gameCode against allowlist before building subdomain URL
  if (!VALID_GAME_CODES.includes(gameCode.toLowerCase())) {
    return '/'; // Fallback to root if invalid
  }
  const protocol = window.location.protocol;
  return `${protocol}//${gameCode.toLowerCase()}${SUBDOMAIN_BASE}`;
}

// ── Component ──────────────────────────────────────────────

export default function MobileMenu({
  open,
  onClose,
  primaryLinks,
  currentGameCode,
  isLoggedIn,
  onTeamClick,
  onLogoutClick,
  unreadNotificationCount = 0,
}: MobileMenuProps) {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const panelRef = useRef<HTMLDivElement>(null);

  useOverlayFocus(panelRef, open);

  // Scroll lock while menu is open
  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow || '';
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // ── Navigation handlers ──────────────────────────────────

  const goAuth = (tab?: string) => {
    onClose();
    navigate(tab === 'register' ? '/auth?tab=register' : '/auth');
  };

  const goProfile = () => {
    onClose();
    if (!user?.userId) return;
    navigate(`/profile/${user.userId}`);
  };

  const goTeam = () => {
    onClose();
    onTeamClick();
  };

  const goNotifications = () => {
    onClose();
    navigate('/notifications');
  };

  const doLogout = () => {
    onClose();
    onLogoutClick();
  };

  const handleGameSelect = (game: GameEntry) => {
    onClose();
    if (game.code === currentGameCode) return;
    window.location.href = buildGameUrl(game.code);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="sl-mobile-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
      onClick={onClose}
    >
      <div ref={panelRef} className="sl-mobile-panel" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className="sl-mobile-header">
          <span className="sl-mobile-header-title">Menu</span>
          <button
            type="button"
            className="sl-mobile-close"
            onClick={onClose}
            aria-label="Close navigation menu"
          >
            ×
          </button>
        </div>

        {/* ── Game Selector ── */}
        <div className="sl-mobile-section-label">Game</div>
        <div className="sl-mobile-game-tiles">
          {AVAILABLE_GAMES.map((game) => {
            const isActive = game.code === currentGameCode;
            return (
              <button
                key={game.code}
                type="button"
                className={`sl-mobile-game-tile ${isActive ? 'sl-mobile-game-tile--active' : ''}`}
                onClick={() => handleGameSelect(game)}
              >
                <span className="sl-mobile-game-tile-name">{game.shortName}</span>
                {isActive && <span className="sl-mobile-game-tile-badge">Active</span>}
              </button>
            );
          })}
        </div>

        <div className="sl-mobile-divider" />

        {/* ── Nav Links ── */}
        <div className="sl-mobile-section-label">Navigation</div>
        <div className="sl-mobile-links">
          {primaryLinks.map((link) => (
            <NavLink
              key={link.label}
              to={link.to}
              exact={link.exact}
              onClick={onClose}
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        {/* ── Social Links ── */}
        <div className="sl-mobile-social">
          <a
            href={SOCIAL_LINKS.x}
            target="_blank"
            rel="noopener noreferrer"
            className="sl-mobile-social-link"
            aria-label="Follow on X"
          >
            <XIcon size={14} />
            <span>Follow on X</span>
          </a>
          <a
            href={SOCIAL_LINKS.discord}
            target="_blank"
            rel="noopener noreferrer"
            className="sl-mobile-social-link sl-mobile-social-link--discord"
            aria-label="Join Discord"
          >
            <DiscordIcon size={16} />
            <span>Join Discord</span>
          </a>
        </div>

        <div className="sl-mobile-divider" />

        {/* ── Account ── */}
        <div className="sl-mobile-section-label">Account</div>
        <div className="sl-mobile-links">
          {isLoggedIn ? (
            <>
              {/* Notifications link with badge */}
              <button
                type="button"
                className="sl-mobile-notif-action"
                onClick={goNotifications}
              >
                <span>Notifications</span>
                {unreadNotificationCount > 0 && (
                  <span className="sl-mobile-notif-badge">
                    {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="sl-mobile-action sl-nav-link sl-nav-link--inactive"
                onClick={goProfile}
              >
                View Profile
              </button>
              <button
                type="button"
                className="sl-mobile-action sl-nav-link sl-nav-link--inactive"
                onClick={goTeam}
              >
                My Team / Create Team
              </button>
              <button
                type="button"
                className="sl-mobile-action sl-mobile-action--danger"
                onClick={doLogout}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="sl-mobile-action sl-nav-link sl-nav-link--inactive"
                onClick={() => goAuth('login')}
              >
                Login
              </button>
              <button
                type="button"
                className="sl-mobile-action sl-nav-link sl-nav-link--inactive"
                onClick={() => goAuth('register')}
              >
                Register
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}